import * as AccountActions from './account.actions';
import { on, createReducer, Action } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import {
  Prescription,
  IAccountProfile,
  IConsultation,
  IAccountAddress,
  IAppointment,
  IPaymentMethod
} from '@care-portals/api-interfaces';

export const ACCOUNT_FEATURE_KEY = 'account';

export interface State {
  paymentMethods: {
    loading: boolean;
    list: IPaymentMethod[];
    error: string;
    showAddPaymentMethod: boolean;
  };
  profile: {
    data: IAccountProfile;
    loading: boolean;
    isEditing: boolean;
    error: string;
  };
  address: {
    data: IAccountAddress;
    loading: boolean;
    isEditing: boolean;
    error: string;
  };
  prescriptions: {
    data: Prescription[];
    loading: boolean;
  };
  consultations: {
    data: IConsultation[];
    loading: boolean;
  };
  appointments: {
    data: IAppointment[];
    loading: boolean;
  };
  availableCredit: number;
}

const initialState: State = {
  paymentMethods: {
    loading: false,
    list: [],
    error: null,
    showAddPaymentMethod: false
  },
  profile: {
    data: null,
    loading: false,
    isEditing: false,
    error: null
  },
  address: {
    data: null,
    loading: false,
    isEditing: false,
    error: null
  },
  prescriptions: {
    data: null,
    loading: false
  },
  consultations: {
    data: null,
    loading: false
  },
  appointments: {
    data: null,
    loading: false
  },
  availableCredit: 0
};

const _reducer = createReducer(
  { ...initialState },

  /* -------------------------------------------------------------------------- */
  /*                                   Account                                  */
  /* -------------------------------------------------------------------------- */
  // @ts-ignore
  on(AccountActions.loadAccount, (state: State) => {
    return {
      ...state,
      profile: {
        data: { ...state.profile.data },
        loading: true
      }
    };
  }),
  on(AccountActions.loadAccountSuccess, (state: State, { accountData }) => {
    const _state: State = cloneDeep(state);
    _state.profile = {
      data: { ...accountData.profile },
      loading: false,
      isEditing: false,
      error: null
    };
    _state.address = {
      data: { ...accountData.address },
      loading: false,
      isEditing: false,
      error: null
    };
    _state.prescriptions = {
      data: accountData.prescriptions,
      loading: false
    };
    _state.consultations = {
      data: accountData.consultations,
      loading: false
    };
    _state.appointments = {
      data: accountData.appointments,
      loading: false
    };
    _state.availableCredit = accountData.availableCredit;
    return _state;
  }),
  // @ts-ignore
  on(AccountActions.loadAccountFailure, (state: State, { error }) => {
    return {
      ...state,
      profile: {
        data: { ...state.profile.data },
        loading: false,
        error: error.message
      }
    };
  }),
  /* -------------------------------------------------------------------------- */
  /*                                   Profile                                  */
  /* -------------------------------------------------------------------------- */
  on(AccountActions.enableEditProfile, (state: State) => {
    const _state: State = cloneDeep(state);
    _state.profile.isEditing = true;
    return _state;
  }),
  on(AccountActions.cancelEditProfile, (state: State) => {
    const _state: State = cloneDeep(state);
    _state.profile.isEditing = false;
    return _state;
  }),
  on(AccountActions.editProfile, (state: State, { req }) => {
    const _state: State = cloneDeep(state);
    _state.profile.loading = true;
    return _state;
  }),
  on(AccountActions.editProfileSuccess, (state: State, { req }) => {
    const _state: State = cloneDeep(state);
    _state.profile = {
      data: { ...req },
      loading: false,
      isEditing: false,
      error: null
    };
    return _state;
  }),
  on(AccountActions.editProfileFailure, (state: State, { error }) => {
    const _state: State = cloneDeep(state);
    _state.profile = {
      data: { ...state.profile.data },
      loading: false,
      isEditing: true,
      error: error.message
    };
    return _state;
  }),

  /* -------------------------------------------------------------------------- */
  /*                                Consultations                               */
  /* -------------------------------------------------------------------------- */

  // on(OrderActions.getOrderHistory, (state: State) => {
  //   const _state = cloneDeep(state);
  //   _state.ordersHistory.loading = true;
  //   return _state;
  // }),
  // on(OrderActions.getOrderHistorySuccess, (state: State, { orderHistory }) => {
  //   const _state = cloneDeep(state);
  //   _state.ordersHistory.loading = false;
  //   _state.ordersHistory.data = orderHistory;
  //   _state.ordersHistory.error = '';
  //   return _state;
  // }),
  // on(OrderActions.getOrderHistoryFailure, (state: State, { error }) => {
  //   const _state = cloneDeep(state);
  //   _state.ordersHistory.loading = false;
  //   _state.ordersHistory.error = error.message;
  //   return _state;
  // }),

  /* -------------------------------------------------------------------------- */
  /*                               PaymentMethods                               */
  /* -------------------------------------------------------------------------- */

  /// LOAD payment methods mutations
  on(AccountActions.loadPaymentMethods, (state: State) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        loading: true,
        error: null,
        list: []
      }
    };
  }),

  on(
    AccountActions.loadPaymentMethodsSuccess,
    (state: State, { paymentMethodsList }) => {
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          loading: false,
          error: null,
          list: paymentMethodsList
        }
      };
    }
  ),

  on(AccountActions.loadPaymentMethodsFailure, (state: State, { error }) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        loading: false,
        error: error.message
      }
    } as State;
  }),

  /// ADD payment methods mutations
  on(AccountActions.showAddPaymentMethod, (state: State) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        showAddPaymentMethod: true
      }
    };
  }),

  on(AccountActions.addPaymentMethodAndAddress, (state: State) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        loading: true,
        error: null
      }
    };
  }),

  on(AccountActions.addPaymentMethod, (state: State) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        loading: true,
        error: null
      }
    };
  }),

  on(AccountActions.addPaymentMethodSuccess, (state: State) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        loading: false,
        error: null,
        showAddPaymentMethod: false
      }
    };
  }),

  on(AccountActions.addPaymentMethodFailure, (state: State, { error }) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        loading: false,
        error: error.message
      }
    };
  }),

  on(AccountActions.removePaymentMethod, (state: State) => {
    return {
      ...state,
      paymentMethods: { ...state.paymentMethods, loading: true }
    };
  }),

  on(AccountActions.removePaymentMethodSuccess, (state: State) => {
    return {
      ...state,
      paymentMethods: { ...state.paymentMethods, loading: false }
    };
  }),

  on(AccountActions.removePaymentMethodFailure, (state: State, { error }) => {
    return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        loading: false,
        error: error.message
      }
    };
  }),

  /* -------------------------------------------------------------------------- */
  /*                                   Address                                  */
  /* -------------------------------------------------------------------------- */
  on(AccountActions.enableEditAddress, (state: State) => {
    const _state: State = cloneDeep(state);
    _state.address.isEditing = true;
    return _state;
  }),
  on(AccountActions.cancelEditAddress, (state: State) => {
    const _state: State = cloneDeep(state);
    _state.address.isEditing = false;
    return _state;
  }),
  on(AccountActions.editAddress, (state: State, { req }) => {
    const _state: State = cloneDeep(state);
    _state.address.loading = true;
    return _state;
  }),
  on(AccountActions.editAddressSuccess, (state: State, { req }) => {
    const _state: State = cloneDeep(state);
    _state.address = {
      data: { ...req },
      loading: false,
      isEditing: false,
      error: null
    };
    return _state;
  }),
  on(AccountActions.editAddressFailure, (state: State, { error }) => {
    const _state: State = cloneDeep(state);
    _state.address = {
      data: { ...state.address.data },
      loading: false,
      isEditing: true,
      error: error.message
    };
    return _state;
  })
);

export function reducer(state: State, action: Action) {
  return _reducer(state, action);
}
